import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames } from '../modules/Constants';

export class GSMModel extends BaseModel {
    SaleablePart = undefined;

    supplementalFormatting(formatting) {
        formatting.columns[9].width = 200;
        formatting.columns[18].width = 300;
        formatting.columns[31].width = 300;
        formatting.columns[37].width = 300;

        return formatting;
    }

    constructor() {
        super();
        this.TabName = TabNames.GSM;
        this.TabIndex = 1;
        this.DataProcessor = new GSMProcessor();
    }

    static PopulateGSM(data){
        let result = new GSMModel();
        let display = [];
        display.push(data.headers);
        data.display.forEach(row => {
            display.push(row)
        })
        result.Headers = data.headers;
        result.Display = display;
        result.Numerics = data.numerics;

        return result;
    }
}

export class GSMProcessor {
    process(data) {
        // -- Define Variables
        let exchangeRates = [];
        let records = [];
        let headers = [];
        let currency = data.currency;
        let logisticsTotalUsageVolumeYears = [];
        let returnableDunnageInvestmentYears = [];
        let toolingRebillYears = [];
        let toolingNonRebillYears = [];
        let vendorExpenseYears = [];

        let snapshots = data.snapshots;
        let version = data.version;
        let study = data.study;

        // -- Get Exchange Rates
        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                exchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        // -- Set Years
        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        let yearsSpending = Array.from(Array((lastYearSpend - firstSOPYear) + 1), (_, x) => x + firstSOPYear);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        logisticsTotalUsageVolumeYears = yearsSpending.map(x => x);
        returnableDunnageInvestmentYears = yearsSpending.map(x => x);

        // -- Set Additional logisticsTotalUsageVolumeYears & returnableDunnageInvestmentYears & vendorToolingRebillYears & vendorToolingNonRebillYears
        snapshots.forEach(snapshot => {
            let logistics = snapshot.Logistics;
            let purchasing = snapshot.Purchases;

            purchasing.forEach(purchase => {
                if(purchase?.VendorTools?.length > 0){
                    //for each vendor tool, get year and add to array of years if doesn't already exist
                    let toolingRebillArray = getToolingRebillArray(purchase);
                    let toolingNonRebillArray = getToolingNonRebillArray(purchase);

                    //let vendorTools = purchase?.VendorTools

                    if(toolingNonRebillArray){
                        toolingNonRebillArray.forEach(toolingNonRebill => {
                            let toolingRebillYear = new Date(toolingNonRebill.Date).getUTCFullYear();
                            if(toolingNonRebillYears.findIndex(x => toolingRebillYear === x) <= -1){
                                toolingNonRebillYears.push(toolingRebillYear);
                            }
                        })
                    }

                    if(toolingRebillArray){
                        toolingRebillArray.forEach(toolingRebill => {
                            let toolingRebillYear = new Date(toolingRebill.Date).getUTCFullYear();
                            if(toolingRebillYears.findIndex(x => toolingRebillYear === x) <= -1){
                                toolingRebillYears.push(toolingRebillYear);
                            }
                        })
                    }
                }
                // KG: add vendor expense years to array for columns
                if(purchase.VendorExpenses?.length > 0){
                    let vendorExpenses = purchase?.VendorExpenses;
                    vendorExpenses.forEach(vendorExpense => {
                        // KG: This values field is an array of vales each with a date and a value field.
                        let vendorExpenseValues = vendorExpense.Value?.Fields[0]?.Cost?.Value?.Fields[0]?.Values
                        vendorExpenseValues.forEach(vendorExpenseValue => {
                            let year = new Date(vendorExpenseValue.Date).getUTCFullYear();
                            if(vendorExpenseYears.findIndex(x => year === x)<= -1){
                                vendorExpenseYears.push(year);
                            }
                        } )
                    })
                }

            })

            logistics.forEach(logistic => {
                let volumes = logistic?.TotalUsageVolume?.Fields[0]?.Values

                volumes?.forEach(volume => {
                    let year = new Date(volume.Date).getUTCFullYear();

                    if (logisticsTotalUsageVolumeYears.findIndex(x => x === year) <= -1) {
                        logisticsTotalUsageVolumeYears.push(year);
                    }
                })

                let returnableDunnage = logistic?.ReturnableDunnageInvestment?.Value?.Fields[0]?.Values;
                returnableDunnage?.forEach(volume => {
                    let year = new Date(volume.Date).getUTCFullYear();

                    if (returnableDunnageInvestmentYears.findIndex(x => x === year) <= -1) {
                        returnableDunnageInvestmentYears.push(year);
                    }
                })
            })
        })
        logisticsTotalUsageVolumeYears.sort();
        returnableDunnageInvestmentYears.sort();
        toolingNonRebillYears.sort();
        toolingRebillYears.sort();
        vendorExpenseYears.sort();

        // -- Set Records
        records = this.setRecords(exchangeRates, currency, snapshots, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, toolingNonRebillYears, toolingRebillYears, vendorExpenseYears);
        // -- Set Headers
        headers = this.configureHeader(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, toolingRebillYears, toolingNonRebillYears, vendorExpenseYears);

        // -- Get Rows
        let result = {
            headers: headers,
            display: this.getDisplay(headers, records, currency, logisticsTotalUsageVolumeYears),
            numerics: this.getNumerics(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, toolingRebillYears,  toolingNonRebillYears, vendorExpenseYears)
        };
        return result;
    }

    setRecords(exchangeRates, currency, snapshots, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, toolingNonRebillYears, toolingRebillYears, vendorExpenseYears){
    let records = [];
    snapshots.forEach(snapshot => {
        //-- Get the Saleable Parts
        let parts = snapshot.SaleableParts;
        let bomForAllSaleables = [];
        parts.forEach(saleablePart => {
            //-- Get the Component Parts
            // this code combines the bom.
            let bom = saleablePart.Bom.value.parts.concat(...saleablePart.Bom.value.partGroups)
            bomForAllSaleables.push(...bom);
        })

        let filteredParts = bomForAllSaleables.filter((p) => p.productionMethod.value.method !== "Make");

        filteredParts.forEach((part) => {
            // KG: if the part is already in the records, we skip this iteration completely, we do not want duplicate rows, we want to group on component number, group name, receiving plant
            var existingPartIndex = records.findIndex(record => record.OptionName === part.groupName && part?.partId?.partNumber === record.ComponentPartNumber && 
                (part?.partId?.drawingRevision === record.ComponentPartRevision) && 
                (record.RecvPlant === part?.productionMethod?.value?.receivingPlant?.value?.plantNumber)  &&
                (part.partId?.suffix === null || record.ComponentPartSuffix === part.partId.suffix)
                
            );
            if(existingPartIndex > -1)
            {
                return;
            }

            //  Get the Purchase for the Comp. Part.
            let purchases = snapshot.Purchases?.filter(purchase => 
                purchase.Part.Number.Fields[0] === part.partId.partNumber &&
                (purchase.Part.DrawingRevision === null || purchase.Part.DrawingRevision.Fields[0]?.Fields[0] === part.partId.drawingRevision) &&
                (purchase.Part.Suffix === null || purchase.Part.Suffix.Fields[0]?.Fields[0] === part.partId.suffix) && 
                (purchase.ReceivingPlant?.Number?.Fields[0] === part?.productionMethod?.value?.receivingPlant?.value?.plantNumber)
            );
            
            let logistics = snapshot.Logistics?.find(logistic => 
                logistic.Part.Number.Fields[0] === part.partId.partNumber &&
                (logistic.Part.DrawingRevision === null || logistic.Part.DrawingRevision.Fields[0]?.Fields[0] === part.partId.drawingRevision) &&
                (logistic.Part.Suffix === null || logistic.Part.Suffix?.Fields[0]?.Fields[0] === part.partId.suffix) &&
                (
                    ( logistic.ProductionMethod?.Fields[0]?.Value?.Fields[0]?.Number?.Fields[0] === part.productionMethod.value?.manufacturingPlant?.value?.plantNumber && logistic.ProductionMethod.Case === part.productionMethod.value.method) || 
                    ( logistic.ProductionMethod?.Fields[0]?.Value?.Fields[0]?.Number?.Fields[0] === part.productionMethod.value?.receivingPlant?.value?.plantNumber && logistic.ProductionMethod.Case === part.productionMethod.value.method)
                )
                )

              
            // KG for each purchase record we will need a new row as each purchase record is going to be tied to a different plant(?)
            if(purchases.length > 0){
                // KG could potentially remove this outer forEach for purchases IF there is only ever one purchase record per part per receiving plant.
                purchases.forEach(purchase => {
                    // KG: if this purchase record (receiving plant) is already existing in the records, then we skip this purchase record.
                    if(records.filter(record => record.OptionName === part.groupName && part?.partId?.partNumber === record.ComponentPartNumber && 
                        (part?.partId.drawingRevision === null || part?.partId?.drawingRevision === record.ComponentPartRevision) && 
                        ( record.RecvPlant === purchase?.ReceivingPlant?.Number?.Fields[0])).length > 0){                          
                            return;
                        }
                    records.push(new GSMRowModel(exchangeRates, currency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, purchase, logistics, toolingRebillYears, toolingNonRebillYears, vendorExpenseYears))        
                })
            }
            else{
                records.push(new GSMRowModel(exchangeRates, currency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, purchases, logistics, toolingRebillYears, toolingNonRebillYears, vendorExpenseYears));
            }
        })})

        return records;
    }

    configureHeader(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, vendorRebillToolingYears, vendorNonRebillToolingYears, vendorExpensesYears) {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.COMPONENT_PART_NUMBER,
            ColumnHeaders.COMPONENT_PART_REV,
            ColumnHeaders.COMPONENT_NAME,
            ColumnHeaders.QTY_PER_PARENT,
            ColumnHeaders.QTY_PER_SALEABLE,
            ColumnHeaders.UOM,
            ColumnHeaders.PRODUCTION_METHOD,
            ColumnHeaders.MFG_PLANT,
            ColumnHeaders.RECV_PLANT,
            ColumnHeaders.DSS_INCLUDED,
            ColumnHeaders.MAINTAIN_FOR_SERVICE,
            ColumnHeaders.PRODCUT_ENGINEER_COMMENTS,
            ColumnHeaders.MATERIAL_TYPE,
            ColumnHeaders.MATERIAL_MASS,
            ColumnHeaders.MATERIAL_MASS_UOM,
            ColumnHeaders.INCOTERMS,
            ColumnHeaders.TITLE_TRANSFER_TERMS,
            ColumnHeaders.LEAD_TIME_WEEKS,
            ColumnHeaders.SUPPLIER_NAME,
            ColumnHeaders.SUPPLIER_LOCATION,
            ColumnHeaders.LOGISTICS_LOCATION,
            ColumnHeaders.PURCHASE_DATA_TYPE,
            ColumnHeaders.SOURCING_RISK,
            ColumnHeaders.REACH_ASSUMPTIONS,
            ColumnHeaders.PURCHASING_COMMENTS,
            ColumnHeaders.PACKAGING_TYPE,
            ColumnHeaders.NESTED,
            ColumnHeaders.EXPORT_READY,
            ColumnHeaders.INVENTORY_DAYS,
            ColumnHeaders.WORKING_DAYS_PER_YEAR,
            ColumnHeaders.LOGISTICS_COMMENTS,
            `${ColumnHeaders.LOGISTICS_TOTAL_USAGE_VOLUME_PREFIX} ${ColumnHeaders.START_YEAR}`
        ]
        
        logisticsTotalUsageVolumeYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.LOGISTICS_TOTAL_USAGE_VOLUME_PREFIX}`);
        });

        if(vendorRebillToolingYears.length > 0 || vendorNonRebillToolingYears.length > 0){
            header.push(ColumnHeaders.TOOLING_CURRENCY);
        }
        
        // KG: added vendor rebill tooling, non rebill tooling, vendor expense years all below
        vendorRebillToolingYears.forEach(year=> {
            header.push(`${year} ${ColumnHeaders.VENDOR_REBILL_ELIGIBLE_TOOLING}`)
        })

        vendorNonRebillToolingYears.forEach(year=> {
            header.push(`${year} ${ColumnHeaders.VENDOR_NON_REBILL_TOOLING}`)
        })
        if(vendorExpensesYears.length > 0){
            header.push(ColumnHeaders.VENDOR_EXPENSE_CURRENCY);
        }
        

        vendorExpensesYears.forEach(year=> {
            header.push(`${year} ${ColumnHeaders.VENDOR_EXPENSE}`)
        }
        )


        header.push(
            ColumnHeaders.MATERIAL_COST,
            ColumnHeaders.MATERIAL_COST_PER_ASM,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.FREIGHT_COST,
            ColumnHeaders.FREIGHT_COST_PER_ASM,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.DUTY_PERCENT,
            ColumnHeaders.DUTY_DRAWBACK_PERCENT,
            `${ColumnHeaders.DUTY_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            ColumnHeaders.EXPENDABLE_DUNNAGE,
            ColumnHeaders.PACKAGING_COST,
            ColumnHeaders.WAREHOUSE_COST,
            ColumnHeaders.WAREHOUSE_COST_PER_ASM,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.PACKAGING_COST_PER_ASM,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.EXPENDABLE_DUNNAGE_PER_ASM,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`
        );


        returnableDunnageInvestmentYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX}`);
        });

        header.push(
            `${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
        );

        returnableDunnageInvestmentYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            `${ColumnHeaders.TOTAL_RETURNABLE_DUNNAGE_INVESTMENT} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.LOGISTICS_VOLUME_ALLOCTION_SHARE,
            `${ColumnHeaders.VOLUME_ALLOCATED_TOTAL_RETURNABLE_DUNNAGE_INVESTMENT} ${ColumnHeaders.CONVERTED_PREFIX}`
        );

        return header;
    }

    getDisplay(headers, records, currency, logisticsTotalUsageVolumeYears ) {
        let display = [];

        display.push(headers);

        records.forEach(record => {
            let row = [
                record.OptionName,
                record.ComponentPartNumber,
                record.ComponentPartRevision,
                record.ComponentName,
                record.QuantityPerParent,
                record.QuantityPerSaleable,
                record.UoM,
                record.ProdMethod,
                record.MfgPlant,
                record.RecvPlant,
                record.DSSIncluded,
                record.MaintainForService,
                record.ProductEngineerComments,
                record.MaterialType,
                record.MaterialMass,
                record.MaterialMassUoM,
                record.Incoterms,
                record.TitleTransferTerms,
                record.LeadTimeWeeks,
                record.SupplierName,
                record.SupplierLocation,
                record.LogisticsLocation,
                record.PurchaseDataType,
                record.SourcingRisk,
                record.ReachAssumptions,
                record.PurchasingComments,
                record.PackagingType,
                record.Nested,
                record.ExportReady,
                record.InventoryDays,
                record.WorkingDaysPerYear,
                record.LogisticsComments,
                logisticsTotalUsageVolumeYears[0],
            ]
            record.LogisticsTotalUsageVolumeValues.forEach(val => {
                row.push(val);
            });
            if(record.ToolingRebillValues.length > 0 || record.ToolingNonRebillValues.length > 0){
                row.push(record.ToolingCurrency);
            }
            record.ToolingRebillValues.forEach(val => {
                row.push(val);
            })

            record.ToolingNonRebillValues.forEach(val => {
                row.push(val);
            })

            if(record.VendorExpenseValues.length > 0){
                row.push(record.VendorExpenseCurrency);
            }
            record.VendorExpenseValues.forEach(val => {
                row.push(val);
            })

            row.push(
                record.MaterialCost,
                record.MaterialCostPerAsm,
                record.MaterialCostCurrency,
                record.MaterialCostConvCurr,
                record.MaterialCostPerAsmConvCurr,
                currency,
                record.FreightCost,
                record.FreightCostPerAsm,
                record.FreightCostCurrency,
                record.FreightCostConvCurr,
                record.FreightCostPerAsmConvCurr,
                currency,
                record.DutyPercent,
                record.DutyDrawbackPercent,
                record.DutyCostPerAsmConvCurr,
                record.ExpendableDunnage,
                record.PackagingCost,
                record.WarehouseCost,
                record.WarehouseCostPerAsm,
                record.WarehouseCostCurrency,
                record.WarehouseCostConvCurr,
                record.WarehouseCostPerAsmConvCurr,
                currency,
                record.PackagingCostPerAsm,
                record.PackagingCostCurrency,
                record.PackagingCostConvCurr,
                record.PackagingCostPerAsmConvCurr,
                currency,
                record.ExpendableDunnagePerAsm,
                record.ExpendableDunnageCurrency,
                record.ExpendableDunnageConvCurr,
                record.ExpendableDunnagePerAsmConvCurr,
                currency,
            )

            record.ReturnableDunnageInvestmentValues.forEach(val => {
                row.push(val);
            });

            row.push(
                record.ReturnableDunnageInvestmentCurrency,
            )

            record.ReturnableDunnageInvestmentValuesConvCurr.forEach(val => {
                row.push(val);
            });

            row.push(
                record.ReturnableDunnageInvestmentCurrencyConvCurrTotal,
                currency,
                record.LogisticsVolumeAllocationShare,
                record.VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr
            )

            display.push(row);
        });

        return display;
    }

    getNumerics(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, vendorToolingRebillYears, vendorToolingNonRebillYears, vendorExpenseYears) {
        let numeric = [];

        //set number formatting
        let logisticsVolumeStart = 33;
        let logisticsVolumeEnd = logisticsTotalUsageVolumeYears.length + logisticsVolumeStart;
        // KG: add vendor tooling rebill numerics
        let vendorToolingRebillStart = 0;
        let vendorToolingRebillEnd = 0;
        if(vendorToolingRebillYears.length > 0 ){
            vendorToolingRebillStart = logisticsVolumeEnd + 1;
            vendorToolingRebillEnd = vendorToolingRebillStart + vendorToolingRebillYears.length;
        }
        let vendorToolingNonRebillStart = 0;
        let vendorToolingNonRebillEnd = 0;
        // KG: Check years array, if no years, no columns, no numerics.
        // if no value is assigned to rebillToolingEnd, then we start the numerics at the end of volume logistics column
        if(vendorToolingNonRebillYears.length > 0){
            if(vendorToolingRebillEnd !== 0){
                vendorToolingNonRebillStart = vendorToolingRebillEnd + 1;
                vendorToolingNonRebillEnd = vendorToolingNonRebillStart + vendorToolingNonRebillYears.length
            }
            else {
                vendorToolingNonRebillStart = logisticsVolumeEnd + 1;
                vendorToolingNonRebillEnd = vendorToolingNonRebillStart + vendorToolingNonRebillYears
            }
        }
        // KG: add vendor Expense numerics
        let vendorExpenseStart = 0;
        let vendorExpenseEnd = 0;
        if(vendorExpenseYears.length > 0){
            // if there is a rebill specified
            if(vendorToolingNonRebillEnd !== 0){
                vendorExpenseStart = vendorToolingNonRebillEnd + 1;
                vendorExpenseEnd = vendorExpenseStart + vendorExpenseYears.length;
            }
            // else if there is a non tooling rebill numeric specified
            else if(vendorToolingRebillEnd !== 0){
                vendorExpenseStart = vendorToolingRebillEnd + 1;
                vendorExpenseEnd = vendorExpenseStart + vendorExpenseYears.length;
            }
            // else there is no vendor tooling, so use the logistics volume end instead
            else{
                vendorExpenseStart = logisticsVolumeEnd + 1;
                vendorExpenseEnd = vendorExpenseStart + vendorExpenseYears.length;
            }
        }
        // Material Cost
        let materialCostStart = 0;
        if(vendorExpenseEnd !== 0){
            materialCostStart = vendorExpenseEnd + 1 ;
        }
        else if(vendorToolingRebillEnd !== 0){
            materialCostStart = vendorToolingRebillEnd + 1;
        }
        else if(vendorToolingNonRebillEnd !== 0){
            materialCostStart = vendorToolingNonRebillEnd + 1;
        }
        else{
            materialCostStart = logisticsVolumeEnd + 1;
        }
        
        let materialCostEnd = materialCostStart + 1;
        let materialCostConvStart = materialCostEnd + 1
        let materialCostConvEnd = materialCostConvStart + 1;
        let freightCostStart = materialCostConvEnd + 1;
        let freightCostEnd = freightCostStart + 1;
        let freightCostConvStart = freightCostEnd + 1
        let freightCostConvEnd = freightCostConvStart + 1;
        let dutyStart = freightCostConvEnd + 1;
        let warehouseCostEnd = dutyStart + 6;
        let warehouseCostConvStart = warehouseCostEnd + 1;
        let warehouseCostConvEnd = warehouseCostConvStart + 1;
        let packagingCostStart = warehouseCostConvEnd + 1;
        let packagingCostEnd = packagingCostStart + 1;
        let packagingCostConvStart = packagingCostEnd + 1;
        let packagingCostConvEnd = packagingCostConvStart + 1;
        let expendableDunnageStart = packagingCostConvEnd + 1;
        let expendableDunnageEnd = expendableDunnageStart + 1;
        let expendableDunnageConvStart = expendableDunnageEnd + 1;
        let expendableDunnageConvEnd = expendableDunnageConvStart + 1;
        let returnableDunnageInvestmentStart = expendableDunnageConvEnd + 1;
        let returnableDunnageInvestmentEnd = returnableDunnageInvestmentStart + returnableDunnageInvestmentYears.length - 1;
        let returnableDunnageInvestmentConvStart = returnableDunnageInvestmentEnd + 1;
        let returnableDunnageInvestmentConvEnd = returnableDunnageInvestmentConvStart + returnableDunnageInvestmentYears.length;
        let logisticsVolumeShareStart = returnableDunnageInvestmentConvEnd + 1;
        let logisticsVolumeShareEnd = logisticsVolumeShareStart + 1;
         
        numeric.push({ start: logisticsVolumeStart, end: logisticsVolumeEnd });
        //KG: if statements to only push these columns as numerics if they are assigned, this allows the dynamic adjustment of years
        if(vendorToolingRebillStart !== 0){
            numeric.push({ start: vendorToolingRebillStart, end: vendorToolingRebillEnd });
        }
        if(vendorToolingNonRebillStart !== 0){
            numeric.push({ start: vendorToolingNonRebillStart, end: vendorToolingNonRebillEnd });
        }
        if(vendorExpenseStart !== 0){
            numeric.push({ start: vendorExpenseStart, end: vendorExpenseEnd });
        }
        
        numeric.push({ start: materialCostStart, end: materialCostEnd });
        numeric.push({ start: materialCostConvStart, end: materialCostConvEnd });
        numeric.push({ start: freightCostStart, end: freightCostEnd });
        numeric.push({ start: freightCostConvStart, end: freightCostConvEnd });
        numeric.push({ start: dutyStart, end: warehouseCostEnd });
        numeric.push({ start: warehouseCostConvStart, end: warehouseCostConvEnd });
        numeric.push({ start: packagingCostStart, end: packagingCostEnd });
        numeric.push({ start: packagingCostConvStart, end: packagingCostConvEnd });
        numeric.push({ start: expendableDunnageStart, end: expendableDunnageEnd });
        numeric.push({ start: expendableDunnageConvStart, end: expendableDunnageConvEnd });
        numeric.push({ start: returnableDunnageInvestmentStart, end: returnableDunnageInvestmentEnd });
        numeric.push({ start: returnableDunnageInvestmentConvStart, end: returnableDunnageInvestmentConvEnd });
        numeric.push({ start: logisticsVolumeShareStart, end: logisticsVolumeShareEnd });

        return numeric;
    }
}


function getToolingRebillArray(purchase) {
    if (purchase.VendorTools) {
        let vendorTools = purchase.VendorTools.map(y => y.Value.Fields[0].Cost.Value.Fields[0].Values.map(z => { return { 'Date': z.Date, 'Value': z.Value, 'Tag': y.Value.Fields[0].Cost.Value.Fields[0].Tag, 'IsRebillable': y.Value.Fields[0].IsRebillable.Value.Fields[0] }; }))
            .reduce(function (a, b) { return a.concat(b); }, []);
        return vendorTools.filter(x => x.IsRebillable);
        //toolingNonRebillArray = vendorTools.filter(x => !x.IsRebillable);
    }
}

function getToolingNonRebillArray(purchase) {
    if (purchase.VendorTools) {
        let vendorTools = purchase.VendorTools.map(y => y.Value.Fields[0].Cost.Value.Fields[0].Values.map(z => { return { 'Date': z.Date, 'Value': z.Value, 'Tag': y.Value.Fields[0].Cost.Value.Fields[0].Tag, 'IsRebillable': y.Value.Fields[0].IsRebillable.Value.Fields[0] }; }))
            .reduce(function (a, b) { return a.concat(b); }, []);
        //return vendorTools.filter(x => x.IsRebillable);
        return vendorTools.filter(x => !x.IsRebillable);
    }
}

export class GSMRowModel {

    //-- Define Properties
    OptionName = "";
    ComponentPartNumber = "";
    ComponentPartRevision = "";
    ComponentName = "";
    QuantityPerParent = "";
    QuantityPerSaleable = "";
    UoM = "";
    ProdMethod = "";
    MfgPlant = "";
    RecvPlant = "";
    DSSIncluded = "";
    MaintainForService = "";
    ProductEngineerComments = "";
    MaterialType = "";
    MaterialMass = "";
    MaterialMassUoM = "";
    Incoterms = "";
    TitleTransferTerms = "";
    LeadTimeWeeks = "";
    SupplierName = "";
    SupplierLocation = "";
    LogisticsLocation = "";
    PurchaseDataType = "";
    SourcingRisk = "";
    ReachAssumptions = "";
    PurchasingComments = "";
    PackagingType = "";
    Nested = "";
    ExportReady = "";
    InventoryDays = "";
    WorkingDaysPerYear = "";
    LogisticsComments = "";
    LogisticsTotalUsageVolumeValues = [];
    MaterialCost = "";
    MaterialCostPerAsm = "";
    MaterialCostCurrency = "";
    MaterialCostConvCurr = "";
    MaterialCostPerAsmConvCurr = "";
    MaterialCostDisplayCurrency = "";
    FreightCost = "";
    FreightCostPerAsm = "";
    FreightCostCurrency = "";
    FreightCostConvCurr = "";
    FreightCostPerAsmConvCurr = "";
    FreightCostDisplayCurrency = "";
    DutyPercent = "";
    DutyDrawbackPercent = "";
    DutyCostPerAsmConvCurr = "";
    ExpendableDunnage = "";
    PackagingCost = "";
    WarehouseCost = "";
    WarehouseCostPerAsm = "";
    WarehouseCostCurrency = "";
    WarehouseCostConvCurr = "";
    WarehouseCostPerAsmConvCurr = "";
    WarehouseCostDisplayCurrency = "";
    PackagingCostPerAsm = "";
    PackagingCostCurrency = "";
    PackagingCostConvCurr = "";
    PackagingCostPerAsmConvCurr = "";
    PackagingCostDisplayCurrency = "";
    ExpendableDunnagePerAsm = "";
    ExpendableDunnageCurrency = "";
    ExpendableDunnageConvCurr = "";
    ExpendableDunnagePerAsmConvCurr = "";
    ExpendableDunnageDisplayCurrency = "";
    ReturnableDunnageInvestmentValues = [];
    ReturnableDunnageInvestmentValuesConvCurr = [];
    ReturnableDunnageInvestmentCurrency = "";
    ReturnableDunnageInvestmentCurrencyConvCurrTotal = 0;
    LogisticsVolumeAllocationShare = "";
    VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr = 0;
    ToolingNonRebillValues = [];
    ToolingRebillValues = [];
    VendorExpenseValues = [];
    ToolingCurrency = "";
    VendorExpenseCurrency = "";
    

    constructor(exchangeRates, exchangeCurrency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, purchase, logistics, toolingRebillYears, toolingNonRebillYears, vendorExpenseYears) {
        //Set Properties
        this.OptionName = part?.groupName;
        this.ComponentPartNumber = part?.partId?.partNumber;
        this.ComponentPartRevision = part?.partId?.drawingRevision;
        this.ComponentPartSuffix = part?.partId?.suffix;
        this.ComponentName = part?.name?.value;
        this.QuantityPerParent = part?.quantityPerParent?.value;
        this.QuantityPerSaleable = part?.quantityPerSaleable;
        this.UoM = part?.unitOfMeasure?.value;
        this.ProdMethod = part?.productionMethod?.value?.method;
        this.MfgPlant = part?.productionMethod?.value?.manufacturingPlant?.value?.plantNumber;
        this.RecvPlant = part?.productionMethod?.value?.receivingPlant?.value?.plantNumber;
        this.DSSIncluded = part?.dssIncluded?.value;
        this.MaintainForService = part?.maintainForService?.value;
        this.ProductEngineerComments = part?.peComments?.value;
        this.MaterialType = part?.materialType?.value;
        this.MaterialMass = Number.isNaN(Number(part?.materialMass?.value)) ? "" : part?.materialMass?.value;
        this.MaterialMassUoM = part?.materialMassUOM?.value;
        this.Incoterms = purchase?.Incoterms?.Value?.Fields[0]?.Case;
        this.TitleTransferTerms = purchase?.TitleTransferTerms?.Value?.Fields[0]?.Case;
        this.LeadTimeWeeks = purchase?.LeadTime?.Value?.Fields[0];
        this.SupplierName = purchase?.SupplierName?.Value?.Fields[0];
        this.SupplierLocation = purchase?.SupplierLocation?.Value?.Fields[0];
        this.LogisticsLocation = purchase?.LogisticsLocation?.Value?.Fields[0]?.Fields[0];
        this.PurchaseDataType = purchase?.PurchaseDataType?.Value?.Fields[0]?.Case;
        this.SourcingRisk = purchase?.SourcingRisk?.Value?.Fields[0];
        this.ReachAssumptions = purchase?.ReachAssumptions?.Value?.Fields[0];
        this.PurchasingComments = purchase?.PurchasingComments?.Value?.Fields[0];
        this.PackagingType = logistics?.PackagingType?.Value?.Fields[0].Case;
        this.Nested = logistics?.Nested?.Value?.Fields[0];
        this.ExportReady = logistics?.ExportReady?.Value?.Fields[0];
        this.InventoryDays = logistics?.InventoryDays?.Value?.Fields[0];
        this.WorkingDaysPerYear = logistics?.WorkingDaysPerYear?.Value?.Fields[0];
        this.LogisticsComments = logistics?.LogisticsComments?.Value?.Fields[0];
        this.LogisticsTotalUsageVolumeValues = [];
        this.DutyPercent = logistics?.DutyPercent?.Value?.Fields[0]?.Values[0]?.Value;
        this.DutyDrawbackPercent = logistics?.DutyDrawbackPercent?.Value?.Fields[0]?.Values[0]?.Value;
        this.ReturnableDunnageInvestmentValues = [];
        this.LogisticsVolumeAllocationShare = Number.isNaN(Number(part?.logisticsVolumeAllocationShare)) ? "" : part?.logisticsVolumeAllocationShare;
        this.ToolingRebillValues = [];
        this.ToolingNonRebillValues = [];
        this.VendorExpenseValues = [];
        this.ToolingCurrency = exchangeCurrency;
        this.VendorExpenseCurrency = exchangeCurrency;

        logisticsTotalUsageVolumeYears.forEach(year => {
            let value = undefined
            if (logistics) {
                value = logistics.TotalUsageVolume.Fields[0].Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;
            }
            this.LogisticsTotalUsageVolumeValues.push(value);
        })
        
        //let vendorTools = undefined;
        let vendorExpensesArray = undefined;
        //let toolingRebillArray = undefined;
        //let toolingNonRebillArray = undefined;
        // KG: get tooling rebill and non-rebill arrays
        let toolingRebillArray = getToolingRebillArray(purchase)
        let toolingNonRebillArray = getToolingNonRebillArray(purchase)

        if(purchase.VendorExpenses){
            vendorExpensesArray = purchase.VendorExpenses.map(y => y.Value.Fields[0].Cost.Value.Fields[0].Values.map(z => {return { 'Date': z.Date, 'Value': z.Value, 'Tag': y.Value.Fields[0].Cost.Value.Fields[0].Tag } }))
            .reduce(function(a,b){ return a.concat(b) }, [])
        }
        
        vendorExpenseYears?.forEach(year => {
            let value = undefined;
            if(vendorExpensesArray){
                let perYearValues = vendorExpensesArray.filter(x=> new Date(x.Date).getUTCFullYear() === year );
                if(perYearValues.length > 0){
                    value = this.exchangeExpense(perYearValues, year, exchangeRates, exchangeCurrency);
                }
            }
            this.VendorExpenseValues.push(value);
        })

        toolingNonRebillYears?.forEach(year => {
            let value = undefined;
            if(toolingNonRebillArray){
                let perYearValues = toolingNonRebillArray.filter(x=> new Date(x.Date).getUTCFullYear() === year );
                if(perYearValues.length > 0){
                    value = this.exchangeExpense(perYearValues, year, exchangeRates, exchangeCurrency);
                }
            }           
            this.ToolingNonRebillValues.push(value);
        })

        // KG add all tooling rebill years to array
        toolingRebillYears?.forEach(year => {
            let value = undefined;
            if(toolingRebillArray){
                let perYearValues = toolingRebillArray.filter(x=> new Date(x.Date).getUTCFullYear() === year );
                if(perYearValues.length > 0){
                    value = this.exchangeExpense(perYearValues, year, exchangeRates, exchangeCurrency);
                }
            }
            this.ToolingRebillValues.push(value);
        })

        let materialCost = purchase?.MaterialCost?.Value?.Fields[0];

        /// KG this is an example of how to use the cost calculations
        let materialCost_calcs = this.exchangeItem(materialCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.MaterialCost = materialCost_calcs.Total;
        this.MaterialCostConvCurr = materialCost_calcs.ConvTotal;
        this.MaterialCostPerAsm = materialCost_calcs.PerAsm;
        this.MaterialCostPerAsmConvCurr = materialCost_calcs.PerAsmConv;
        this.MaterialCostCurrency = materialCost?.Tag;

        let freightCost = logistics?.FreightCost?.Value?.Fields[0];
        let freightCost_calcs = this.exchangeItem(freightCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.FreightCost = freightCost_calcs.Total;
        this.FreightCostConvCurr = freightCost_calcs.ConvTotal;
        this.FreightCostPerAsm = freightCost_calcs.PerAsm;
        this.FreightCostPerAsmConvCurr = freightCost_calcs.PerAsmConv;
        this.FreightCostCurrency = freightCost?.Tag;

        let expendableDunnage = logistics?.ExpendableDunnage?.Value?.Fields[0];
        let expendableDunnage_calcs = this.exchangeItem(expendableDunnage, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.ExpendableDunnage = expendableDunnage_calcs.Total;
        this.ExpendableDunnageConvCurr = expendableDunnage_calcs.ConvTotal;
        this.ExpendableDunnagePerAsm = expendableDunnage_calcs.PerAsm;
        this.ExpendableDunnagePerAsmConvCurr = expendableDunnage_calcs.PerAsmConv;
        this.ExpendableDunnageCurrency = expendableDunnage?.Tag;

        let packagingCost = logistics?.PackagingCost?.Value?.Fields[0];
        let packagingCost_calcs = this.exchangeItem(packagingCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.PackagingCost = packagingCost_calcs.Total;
        this.PackagingCostConvCurr = packagingCost_calcs.ConvTotal;
        this.PackagingCostPerAsm = packagingCost_calcs.PerAsm;
        this.PackagingCostPerAsmConvCurr = packagingCost_calcs.PerAsmConv;
        this.PackagingCostCurrency = packagingCost?.Tag;

        let warehouseCost = logistics?.WarehouseCost?.Value?.Fields[0];
        let warehouseCost_calcs = this.exchangeItem(warehouseCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.WarehouseCost = warehouseCost_calcs.Total;
        this.WarehouseCostConvCurr = warehouseCost_calcs.ConvTotal;
        this.WarehouseCostPerAsm = warehouseCost_calcs.PerAsm;
        this.WarehouseCostPerAsmConvCurr = warehouseCost_calcs.PerAsmConv;
        this.WarehouseCostCurrency = warehouseCost?.Tag;

        returnableDunnageInvestmentYears.forEach(year => {
            let cost = undefined;
            let convCost = undefined;

            if (logistics) {
                cost = logistics?.ReturnableDunnageInvestment?.Value?.Fields[0].Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;
                this.ReturnableDunnageInvestmentCurrency = logistics?.ReturnableDunnageInvestment?.Value?.Fields[0]?.Tag

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.ReturnableDunnageInvestmentCurrency);

                if (!(exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined)) {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.ReturnableDunnageInvestmentCurrencyConvCurrTotal += convCost;
                }
            }

            this.ReturnableDunnageInvestmentValues.push(cost);
            this.ReturnableDunnageInvestmentValuesConvCurr.push(convCost);
        })

        this.VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr = this.ReturnableDunnageInvestmentCurrencyConvCurrTotal * this.LogisticsVolumeAllocationShare;
        let dutyPerAsm = (this.MaterialCostPerAsmConvCurr + this.FreightCostPerAsmConvCurr + this.WarehouseCostPerAsmConvCurr + this.PackagingCostPerAsmConvCurr + this.ExpendableDunnagePerAsmConvCurr) * this.DutyPercent
        this.DutyCostPerAsmConvCurr = Number.isNaN(dutyPerAsm) ? 0 : dutyPerAsm;

    }

    exchangeExpense(inputArray, year, exchangeRates, exchangeCurrency){
        let cost = undefined;
        let convCost = undefined;
        let value = 0;
        inputArray.forEach(val => {
            cost = val.Value
            
            let originalCurrency = val.Tag;
            let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
            let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === originalCurrency);

            if (!(exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined)) {
                
                convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                value += convCost;
            }
        })
        return value;
    }

    exchangeItem(obj, qtyPerSaleable, exchangeRates, exchangeCurrency) {
        let total = 0;
        let convTotal = 0;
        let years = [];
        let convCosts = [];
        let costs = []
        let perAsm = 0;
        let perAsmConv = 0;

        obj?.Values.forEach(item => {
            let year = new Date(item.Date).getUTCFullYear();

            years.push(year);

            let cost = item?.Value ?? undefined;
            let convCost = undefined;

            let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
            let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === obj.Tag);

            if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                if (cost !== undefined) {
                    convCosts.push("Error");
                    costs.push("Error");
                } else {
                    convCosts.push("");
                    costs.push("");
                }
            } else {
                convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                convCosts.push(convCost);
                costs.push(cost);
                convTotal += convCost;
                total += cost;
            }

        });

        perAsm = Number.isNaN(total * qtyPerSaleable) ? 0 : total * qtyPerSaleable;
        perAsmConv = Number.isNaN(convTotal * qtyPerSaleable) ? 0 : convTotal * qtyPerSaleable;

        let result = {}
        result.Total = total;
        result.ConvTotal = convTotal;
        result.Years = years;
        result.ConvCosts = convCosts;
        result.Costs = costs;
        result.PerAsm = perAsm;
        result.PerAsmConv = perAsmConv;

        return result;
    }
}